import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { api } from "../lib/api";
import { CosmicBackground } from "../components/CosmicBackground";
import { ArrowLeft, Loader2, MapPin } from "lucide-react";

const PLANET_COLORS = {
  Sun: "#FFD93D", Moon: "#E0E7FF", Mercury: "#A78BFA", Venus: "#F472B6",
  Mars: "#F87171", Jupiter: "#FBBF24", Saturn: "#94A3B8",
};

export default function Astrocartography() {
  const navigate = useNavigate();
  const mapEl = useRef(null);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/astrocartography/me");
        setData(data);
      } catch (e) {
        setError(e?.response?.data?.detail || "Could not load map");
      }
    })();
  }, []);

  useEffect(() => {
    if (!data || !mapEl.current || !data.mapbox_token) return;
    mapboxgl.accessToken = data.mapbox_token;
    const map = new mapboxgl.Map({
      container: mapEl.current,
      style: "mapbox://styles/mapbox/dark-v11",
      center: [data.birth?.lng || 0, data.birth?.lat || 20],
      zoom: 1.5,
      projection: "naturalEarth",
    });
    map.on("load", () => {
      if (data.birth?.lat != null) {
        const el = document.createElement("div");
        el.style.cssText = "width:18px;height:18px;border-radius:50%;background:#D4AF37;box-shadow:0 0 18px #D4AF37;border:2px solid #05070A;";
        new mapboxgl.Marker(el).setLngLat([data.birth.lng, data.birth.lat]).addTo(map);
      }
      (data.lines || []).forEach((ln, i) => {
        const color = PLANET_COLORS[ln.planet] || "#D4AF37";
        map.addSource(`line-${i}`, {
          type: "geojson",
          data: { type: "Feature", geometry: { type: "LineString", coordinates: [[ln.lng, -85], [ln.lng, 85]] } },
        });
        map.addLayer({
          id: `line-${i}`,
          type: "line",
          source: `line-${i}`,
          paint: { "line-color": color, "line-width": ln.type === "MC" ? 1.5 : 0.8, "line-opacity": 0.65, "line-dasharray": ln.type === "MC" ? [1] : [2, 2] },
        });
      });
    });
    return () => map.remove();
  }, [data]);

  return (
    <CosmicBackground>
      <header className="max-w-7xl mx-auto px-6 md:px-12 py-6 flex items-center justify-between">
        <button onClick={() => navigate("/dashboard")} className="text-[#94A3B8] hover:text-white text-sm flex items-center gap-1" data-testid="astro-back-btn">
          <ArrowLeft size={14} /> Dashboard
        </button>
        <span className="font-serif-mystic text-xl tracking-wide">AuraQuest</span>
      </header>
      <main className="max-w-6xl mx-auto px-6 md:px-12 pb-24" data-testid="astrocartography-page">
        <div className="mb-8">
          <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-2">Astrocartography</p>
          <h1 className="font-serif-mystic text-4xl sm:text-5xl tracking-tight">Where you came alive on Earth</h1>
          <p className="text-[#94A3B8] mt-2 max-w-2xl flex items-center gap-2"><MapPin size={14} /> {data?.birth?.city || "—"}</p>
        </div>
        {error ? (
          <div className="rounded-xl border border-red-400/20 bg-red-500/5 p-6 text-red-300">{error}</div>
        ) : !data ? (
          <div className="h-[500px] grid place-items-center"><Loader2 className="animate-spin text-[#D4AF37]" /></div>
        ) : (
          <div ref={mapEl} className="w-full h-[60vh] rounded-2xl border border-white/10 overflow-hidden" data-testid="astro-map" />
        )}
        {data?.lines?.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-3 text-xs text-[#94A3B8]">
            {Object.entries(PLANET_COLORS).map(([n, c]) => (
              <span key={n} className="flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-full" style={{ background: c }} />{n}</span>
            ))}
            <span className="ml-4">Solid = MC · Dashed = IC</span>
          </div>
        )}
      </main>
    </CosmicBackground>
  );
}
